import React from 'react';
import PropTypes from 'prop-types';
import { CHART_COLORS, EChartsRenderer } from 'components/charts';
import 'components/charts/chart.css';
import { ShowHide } from 'components/layout';
import classNames from 'classnames';
import { NonIdealState } from '@blueprintjs/core';

export function PieChart({
    title,
    items,
    loading = false,
    noDataMessage = "No data to display",
    minimal = false,

    legend = {
        orient: 'vertical',
        left: 'right',
        top: 'center'
    },

    tooltip = {
        show: true
    },

    centerValue = null,
    onSliceClick = null
}) {
    const getColourByIndex = (index) => {
        return CHART_COLORS[index % CHART_COLORS.length];
    }

    // ECharts draws the legend on the same canvas as the series, so when the legend
    // sits at the bottom we have to shrink the series and shift it up to avoid overlap.
    const legendIsBottom = legend?.orient === 'horizontal' || legend?.bottom !== undefined;

    // A legend down the right-hand side takes its width out of the same canvas, so the series
    // is shifted left to sit in the space that is left rather than under the legend.
    const legendIsRight = legend?.show !== false && !legendIsBottom;
    const seriesCenterHorizontal = legendIsRight ? '30%' : '50%';
    const seriesCenterVertical = legendIsBottom ? '40%' : '50%';
    const seriesCenter = [seriesCenterHorizontal, seriesCenterVertical];
    const seriesRadius = legendIsBottom ? ['40%', '75%'] : ['40%', '90%'];

    const showCenterOverlay = (centerValue !== null && centerValue !== undefined) && (items?.length || loading);

    return (
        <div className="chart-container">
            <ShowHide evaluator={!minimal}
            show={( <span className='chart-title'>CHART</span> )}
            />
            <h3 className={classNames({ 'spacer-bottom': !minimal, 'no-margin': minimal})}>{title}</h3>
            <ShowHide
                evaluator={items?.length > 0 || loading}
                show={(
                    <div className={classNames('pie-chart-canvas', {'bp6-skeleton': loading })}>
                        <EChartsRenderer
                            className="pie-chart"
                            option={{
                                tooltip: tooltip,
                                legend: legend,
                                series: [
                                    {
                                        type: "pie",
                                        center: seriesCenter,
                                        radius: seriesRadius,
                                        avoidLabelOverlap: false,
                                        label: {
                                            show: false,
                                            position: 'center'
                                        },
                                        cursor: onSliceClick ? 'pointer' : 'default',
                                        data: items.map((item, index) => ({
                                            value: item.value,
                                            name: item.label,
                                            itemStyle: {
                                                color: item.color ?? getColourByIndex(index)
                                            }
                                        }))
                                    },
                                ],
                            }}
                            onEvents={onSliceClick ? {
                                click: (params) => {
                                    if (params.componentType !== 'series') {
                                        return;
                                    }

                                    // Stop the native event bubbling so a wrapping
                                    // onClick doesn't also fire on a slice click.
                                    const nativeEvent = params.event?.event;
                                    if (nativeEvent?.stopPropagation) {
                                        nativeEvent.stopPropagation();
                                    }

                                    onSliceClick(items[params.dataIndex]);
                                }
                            } : null}
                        />
                        {showCenterOverlay && !loading && (
                            <div
                                className="pie-chart-center-overlay"
                                style={{ top: seriesCenterVertical, left: seriesCenterHorizontal }}
                            >
                                <p className="pie-chart-center-value">{centerValue}</p>
                            </div>
                        )}
                    </div>
                )}
                hide={<NonIdealState
                    title={''}
                    className='spacer-bottom-small'
                    description={noDataMessage}
                />}
            />
        </div>
    );
}

PieChart.propTypes = {
    title: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired,
            color: PropTypes.string
        })
    ).isRequired,
    loading: PropTypes.bool,
    noDataMessage: PropTypes.string,
    minimal: PropTypes.bool,
    legend: PropTypes.object,
    tooltip: PropTypes.object,
    centerValue: PropTypes.node,
    onSliceClick: PropTypes.func
};
